import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { EpisodeListFilters as Filters, EpisodeSummary } from "@/api";
import { useWindowHotkeys } from "@/components/keyboard";
import { MultiChannelVideoPlayer, type VideoChannelSource } from "@/components/video";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { episodeFilesStepSeconds, episodeFilesToChannels } from "./episodeChannels";
import { EpisodeFilterLane } from "./EpisodeFilterLane";
import { EpisodeList } from "./EpisodeList";
import {
  excludedShownByFilters,
  filterVisibleEpisodes,
  shouldHideExcluded,
} from "./episodeReviewState";
import type { FileBrowserSource } from "./fileBrowserSource";
import { useEpisodeFiles } from "./useEpisodeFiles";
import { useEpisodePage } from "./useEpisodePage";
import styles from "./EpisodesViewer.module.css";

// 채널이 없을 때(파일 미적재, 대표 영상 미상) 쓰는 고정 빈 배열.
// 렌더마다 새 `[]` 를 만들지 않으므로 재생 엔진이 불필요하게 리셋되지 않는다.
const EMPTY_CHANNELS: readonly VideoChannelSource[] = [];
const EMPTY_EPISODES: readonly EpisodeSummary[] = [];

/**
 * 목록과 영상의 배치. 폭 예산이 다르면 배치도 다르다.
 *
 * - `primary`: 좌 목록(200px) / 우 영상. 탭 하나 전체를 쓰는 위치에 쓴다.
 * - `compact`: 목록을 위에 두고 영상이 남는 폭 전체를 쓴다. 데이터셋 리뷰 탭의 표본 레인처럼
 *   좁은 세로 띠에 쓴다. 200px 목록을 옆에 두면 카메라가 네 대인 데이터셋에서 격자 한 칸을
 *   읽을 수 없다.
 */
export type EpisodesViewerMode = "primary" | "compact";

export type EpisodesViewerProps = {
  // 정본 소스. 목록과 파일 조회 endpoint 는 소스가 소유한다.
  source: FileBrowserSource;
  resourceUuid: string;
  mode?: EpisodesViewerMode;
};

// 에피소드 탐색 화면. 좌: 에피소드 목록(무한 스크롤 누적) / 우: 선택 에피소드의 카메라 영상.
// 목록 적재(useEpisodePage), 파일 로드(useEpisodeFiles), 선택 상태를 직접 소유한다.
// 부모가 준 높이를 채우는 fill 구조 하나만 그린다.
// source/resource 가 바뀔 때 상태 격리는 소비처의 kind+UUID key remount 가 담당한다.
// 그래서 내부에 같은 목적의 reset effect 를 두지 않는다.
export function EpisodesViewer({
  source,
  resourceUuid,
  mode = "primary",
}: EpisodesViewerProps): ReactNode {
  const [filters, setFilters] = useState<Filters>({});
  const { state, total, hasMore, loadingMore, loadMore, reload } = useEpisodePage(
    source,
    resourceUuid,
    {
      accumulate: true,
      filters,
    },
  );
  const [selected, setSelected] = useState<EpisodeSummary | null>(null);
  const [includeExcluded, setIncludeExcluded] = useState(false);
  const loaded = state.status === "success" ? state.data : EMPTY_EPISODES;
  const hideExcluded = shouldHideExcluded(includeExcluded, filters);
  const items = useMemo(() => filterVisibleEpisodes(loaded, !hideExcluded), [loaded, hideExcluded]);

  // 목록이 오면 첫 에피소드를 연다. 열자마자 볼 에피소드가 있어야 하고, ↑/↓ 이동에도 시작
  // 위치가 필요하다. 아직 아무것도 고르지 않았을 때만 선택한다. 그래서 사용자의 선택을 덮지 않는다.
  //
  // 이 자동 선택은 제출 창 커서 미리보기에서 데이터셋을 이동할 때마다 영상 다운로드를 시작한다.
  // 그러면 host 당 연결 슬롯을 차지한다(`1f15a010`). 그 커서에는 디바운스가 있어
  // (`PREVIEW_DEBOUNCE_MS`) 커서가 멈춘 데이터셋만 로드한다. 슬롯 부족이 다시 보이면
  // 먼저 그 디바운스 값을 늘린다.
  useEffect(() => {
    if (selected !== null || items.length === 0) {
      return;
    }
    setSelected(items[0]);
  }, [items, selected]);

  const { files, reportVideoError } = useEpisodeFiles(source, selected);
  // 파일이 그대로면 재생 엔진에 같은 channels 참조를 전달한다.
  const channels = useMemo<readonly VideoChannelSource[]>(
    () => (files.status === "success" ? episodeFilesToChannels(files.data) : EMPTY_CHANNELS),
    [files],
  );
  // 재생 바 한 칸은 한 프레임이다. 카탈로그에 fps 가 없는 에피소드는 undefined 이므로 c6 기본
  // step 을 쓴다. 리뷰에서 fps 를 채우면 다음 조회부터 프레임 단위가 된다.
  const stepSeconds = useMemo(
    () => (files.status === "success" ? episodeFilesStepSeconds(files.data) : undefined),
    [files],
  );

  // ↑/↓ 로 목록을 이동한다. 순서는 **화면에 보이는 순서**(필터·숨김을 적용한 items)이므로
  // 이 컴포넌트가 이동을 소유한다. 이 컴포넌트는 창 밖에서도 마운트될 수 있으므로 창 셸 콜백에
  // 등록하지 않는다(FileBrowser 와 같은 방식). 리스너와 가드는 공용 컨트롤러가 소유한다.
  const viewerRef = useRef<HTMLDivElement | null>(null);
  // 핸들러가 렌더 시점 값을 ref 로 읽는다. 그래서 리스너를 다시 등록하지 않아도 최신 순서를 쓴다.
  const navRef = useRef({ items, selected, hasMore, loadMore });
  navRef.current = { items, selected, hasMore, loadMore };

  const move = useCallback((direction: 1 | -1) => {
    const current = navRef.current;
    if (current.items.length === 0) {
      return;
    }
    const index = current.items.findIndex((item) => item.uuid === current.selected?.uuid);
    const base = index >= 0 ? index : 0;
    const nextIndex = Math.max(0, Math.min(current.items.length - 1, base + direction));
    // 아래로 이동하다 마지막 구간(길이의 80%)에 들어가면 다음 페이지를 미리 요청한다.
    // 커서는 행 단위로 움직이므로 인덱스로 판정한다. 스크롤로 이동할 때는 목록 끝의 trigger 가
    // 같은 일을 한다.
    if (direction === 1 && current.hasMore && nextIndex >= Math.floor(current.items.length * 0.8)) {
      current.loadMore();
    }
    const next = current.items[nextIndex];
    if (next !== undefined) {
      setSelected(next);
    }
  }, []);

  useWindowHotkeys({
    surfaceRef: viewerRef,
    bindings: [
      { key: "ArrowUp", onPress: () => move(-1) },
      { key: "ArrowDown", onPress: () => move(1) },
    ],
  });

  return (
    <div className={styles.viewer} data-mode={mode} ref={viewerRef}>
      <EpisodeList
        state={state}
        items={items}
        loadedCount={loaded.length}
        total={total}
        hasMore={hasMore}
        loadingMore={loadingMore}
        includeExcluded={includeExcluded}
        onIncludeExcludedChange={setIncludeExcluded}
        includeExcludedForced={excludedShownByFilters(filters)}
        filters={
          // compact 는 세로 공간이 제한되므로 필터 줄을 숨긴다. 필터가 필요하면 에피소드/파일
          // 탭에서 적용한다. 필터 없이 여는 소비처는 이 prop 을 생략한다. 이것이 계약이다.
          mode === "compact" ? undefined : (
            <EpisodeFilterLane
              // seam 구현체가 `datasetFileBrowserSource` 하나뿐이므로 현재 resource 는 dataset 이다.
              datasetUuid={resourceUuid}
              value={filters}
              onChange={setFilters}
              disabled={state.status === "loading"}
            />
          )
        }
        selectedUuid={selected?.uuid ?? null}
        onSelect={setSelected}
        onLoadMore={loadMore}
        onRetry={reload}
      />
      <section className={styles.detail} aria-label="에피소드 상세">
        {/* 영상을 우선한다. 에피소드명, meta, "Cameras" 라벨은 두지 않는다.
            이름은 좌측 목록에, 카메라 이름은 각 영상 캡션에 있다. */}
        {selected ? (
          files.status === "loading" || files.status === "idle" ? (
            <LoadingState>영상을 불러오고 있습니다.</LoadingState>
          ) : files.status === "error" ? (
            <EmptyState tone="error">{files.message}</EmptyState>
          ) : (
            <MultiChannelVideoPlayer
              channels={channels}
              onChannelError={reportVideoError}
              emptyMessage="재생할 카메라 영상이 없습니다."
              // 공간이 좁으면 플레이어도 compact 배치를 알아야 한다. 격자만 스크롤하고 재생 바를
              // 아래에 고정하는 동작은 플레이어가 소유한다.
              mode={mode}
              // 좁은 레인은 한 열이다. 카메라가 세 대 이상이면 한 번에 하나만 보인다.
              // 그래서 타일 높이를 줄여 여러 대를 함께 보게 한다. 한두 대이면 줄이지 않는다.
              // 그 경우 레인 폭을 그대로 쓰는 것이 더 잘 보인다.
              maxHeight={mode === "compact" && channels.length > 2 ? "11rem" : undefined}
              // 표본 레인에서는 장면 하나를 골라 판정한다. 그래서 프레임 단위 이동이 필요하다.
              stepSeconds={stepSeconds}
              // compact 는 레인 폭이 좁아 타일 하나가 작다. 그래서 제자리 확대와 [크게 보기] 를
              // 함께 켠다. 넓은 primary 는 타일만으로 충분히 읽을 수 있다.
              zoomable={mode === "compact"}
            />
          )
        ) : (
          <div className={styles.detailEmpty}>
            <EmptyState>에피소드를 선택해 주세요.</EmptyState>
          </div>
        )}
      </section>
    </div>
  );
}
